(() => {
  const ROOT = window.Shopify?.routes?.root || "/";

  function formQuantity(form) {
    const productInfo = form?.closest(".product-info") || document.querySelector(".product-info");
    const quantityInput =
      productInfo?.querySelector('[data-block-type="quantity-selector"] [name="quantity"]') ||
      productInfo?.querySelector(".quantity-selector__input") ||
      (form?.id ? document.querySelector(`[name="quantity"][form="${form.id}"]`) : null) ||
      form?.querySelector('[name="quantity"]');
    const value = Number(quantityInput?.value || 1);
    return Number.isFinite(value) && value > 0 ? value : 1;
  }

  function formProperties(form) {
    if (!form) return null;
    const properties = {};
    new FormData(form).forEach((value, key) => {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && value !== "") properties[match[1]] = value;
    });
    return Object.keys(properties).length ? properties : null;
  }

  async function addItems(items, form) {
    const sectionsToBundle = [];
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:prepare-bundled-sections", {
        bubbles: true,
        detail: { sections: sectionsToBundle }
      })
    );

    const mainId = form?.querySelector('[name="id"]')?.value;
    const response = await fetch(`${ROOT}cart/add.js`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest"
      },
      body: JSON.stringify({
        items,
        sections: sectionsToBundle.join(","),
        sections_url: mainId ? `${ROOT}variants/${mainId}` : window.location.pathname
      })
    });
    const responseJson = await response.json();
    if (!response.ok) {
      throw new Error(responseJson.description || responseJson.message || "Add to cart failed");
    }

    if (
      window.themeVariables?.settings?.cartType === "page" ||
      window.themeVariables?.settings?.pageType === "cart"
    ) {
      window.location.href = `${ROOT}cart`;
      return responseJson;
    }

    const cartContent = await (await fetch(`${ROOT}cart.js`)).json();
    cartContent.sections = responseJson.sections;
    form?.dispatchEvent(
      new CustomEvent("variant:add", {
        bubbles: true,
        detail: {
          items: responseJson.items || [responseJson],
          cart: cartContent
        }
      })
    );
    document.documentElement.dispatchEvent(
      new CustomEvent("cart:change", {
        bubbles: true,
        detail: {
          baseEvent: "variant:add",
          onSuccessDo: "force_open_drawer",
          cart: cartContent
        }
      })
    );
    return responseJson;
  }

  class ProductAddOns extends HTMLElement {
    connectedCallback() {
      this.actionStyle = this.dataset.actionStyle || "toggle";
      this.form = this.resolveForm();
      this.onToggle = this.syncForm.bind(this);
      this.onButtonClick = this.onButton.bind(this);
      this.onSubmit = this.onCartFormSubmit.bind(this);

      this.querySelectorAll("[data-add-on-toggle]").forEach((input) => {
        input.addEventListener("change", this.onToggle);
      });
      this.querySelectorAll("[data-add-on-button]").forEach((button) => {
        button.addEventListener("click", this.onButtonClick);
      });

      if (this.actionStyle === "toggle") {
        this.syncForm();
        document.addEventListener("submit", this.onSubmit, true);
      }
    }

    disconnectedCallback() {
      this.querySelectorAll("[data-add-on-toggle]").forEach((input) => {
        input.removeEventListener("change", this.onToggle);
      });
      this.querySelectorAll("[data-add-on-button]").forEach((button) => {
        button.removeEventListener("click", this.onButtonClick);
      });
      document.removeEventListener("submit", this.onSubmit, true);
    }

    resolveForm() {
      return (
        this.closest('form[action*="/cart/add"]') ||
        this.closest(".product-info")?.querySelector('form[action*="/cart/add"]') ||
        this.closest("product-form")?.querySelector('form[action*="/cart/add"]') ||
        document.querySelector('[data-block-type="buy-buttons"] form[action*="/cart/add"]') ||
        document.querySelector('product-form form[action*="/cart/add"]:not(product-sticky-bar form)')
      );
    }

    isInterceptForm(form) {
      if (!(form instanceof HTMLFormElement)) return false;
      if (!form.getAttribute("action")?.includes("/cart/add")) return false;
      if (!this.form || !this.form.isConnected) this.form = this.resolveForm();
      return form === this.form || Boolean(form.closest("product-sticky-bar"));
    }

    selectedAddOns() {
      return [...this.querySelectorAll("[data-add-on-toggle]:checked")]
        .map((input) => Number(input.dataset.variantId))
        .filter((id) => Number.isFinite(id) && id > 0)
        .map((id) => ({ id, quantity: 1 }));
    }

    syncForm() {
      if (!this.form) return;

      this.form.querySelectorAll("[data-product-add-on-field]").forEach((field) => field.remove());

      this.selectedAddOns().forEach((item, index) => {
        const idInput = document.createElement("input");
        idInput.type = "hidden";
        idInput.name = `items[${index + 1}][id]`;
        idInput.value = String(item.id);
        idInput.setAttribute("data-product-add-on-field", "");
        this.form.append(idInput);

        const quantityInput = document.createElement("input");
        quantityInput.type = "hidden";
        quantityInput.name = `items[${index + 1}][quantity]`;
        quantityInput.value = "1";
        quantityInput.setAttribute("data-product-add-on-field", "");
        this.form.append(quantityInput);
      });
    }

    async onCartFormSubmit(event) {
      const form = event.target;
      if (!this.isInterceptForm(form)) return;

      const extras = this.selectedAddOns();
      if (extras.length === 0) return;

      event.preventDefault();
      event.stopImmediatePropagation();

      if (!this.form || !this.form.isConnected) this.form = this.resolveForm();
      const sourceForm = this.form && this.form !== form ? this.form : form;
      const mainQuantity = formQuantity(sourceForm);

      if (form !== sourceForm) {
        const currentId = sourceForm.querySelector('[name="id"]')?.value;
        const stickyVariantInput = form.querySelector('[name="id"]');
        if (currentId && stickyVariantInput) stickyVariantInput.value = currentId;
        const stickyQuantityInput = form.querySelector('[name="quantity"]');
        if (stickyQuantityInput) stickyQuantityInput.value = String(mainQuantity);
      }

      const mainId = Number(sourceForm.querySelector('[name="id"]')?.value || form.querySelector('[name="id"]')?.value);
      if (!mainId) return;

      const submitButtons = Array.from(form.elements).filter((el) => el.type === "submit");
      if (submitButtons.some((button) => button.getAttribute("aria-busy") === "true")) return;

      submitButtons.forEach((button) => button.setAttribute("aria-busy", "true"));
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      const mainItem = { id: mainId, quantity: mainQuantity };
      const sellingPlan = form.querySelector('[name="selling_plan"]')?.value || sourceForm.querySelector('[name="selling_plan"]')?.value;
      if (sellingPlan) mainItem.selling_plan = Number(sellingPlan);
      const properties = formProperties(form) || formProperties(sourceForm);
      if (properties) mainItem.properties = properties;

      try {
        await addItems([mainItem, ...extras], form);
      } catch (error) {
        form.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: String(error.message || error) }
          })
        );
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } finally {
        submitButtons.forEach((button) => button.removeAttribute("aria-busy"));
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }

    async onButton(event) {
      const button = event.currentTarget;
      const variantId = Number(button.dataset.variantId);
      if (!variantId || button.getAttribute("aria-busy") === "true") return;

      button.setAttribute("aria-busy", "true");
      document.documentElement.dispatchEvent(new CustomEvent("theme:loading:start", { bubbles: true }));

      try {
        await addItems([{ id: variantId, quantity: 1 }], this.form);
      } catch (error) {
        this.form?.dispatchEvent(
          new CustomEvent("cart:error", {
            bubbles: true,
            detail: { error: String(error.message || error) }
          })
        );
        document.dispatchEvent(new CustomEvent("cart:refresh"));
      } finally {
        button.removeAttribute("aria-busy");
        document.documentElement.dispatchEvent(new CustomEvent("theme:loading:end", { bubbles: true }));
      }
    }
  }

  if (!customElements.get("product-add-ons")) {
    customElements.define("product-add-ons", ProductAddOns);
  }
})();
